import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

/**
 * Button — Clones Design System (ADR-0030).
 *
 * Variants resolve to semantic tokens from `themes/clones-design-tokens.css`:
 *   - default     → Pulse (primary accent for the selected direction)
 *   - destructive → Bad   (muted-red, editorial palette)
 *   - outline     → hairline border on paper, paper-3 hover
 *   - secondary   → paper-3 raised surface
 *   - ghost       → transparent rest, paper-3 hover
 *   - link        → Pulse text, underline on hover
 *   - success     → Ok (muted-green)
 */
/*
 * The handoff defines a dashed-twin clone hover via ::before but
 * Tailwind's preflight (`*::before { border: 0 ... }`) strips the
 * dashed border, leaving only an empty rectangle. Mixing the c-button
 * class with the cva variants also doubled padding / border-radius
 * (visible artifacts on Outline / Ghost). Re-applied as a Tailwind
 * pseudo-utility scoped to *this* component so it can't leak; the
 * c-button class from clones-components.css remains available for
 * explicit opt-in but is no longer auto-attached here.
 */
const buttonVariants = cva(
  [
    "relative min-h-[var(--control-size)] min-w-[var(--touch-target-size)] inline-flex items-center justify-center gap-[var(--control-gap)] whitespace-nowrap rounded-[var(--r-3)] text-sm font-medium",
    "transition-colors duration-[var(--dur-2)] ease-[var(--ease-out)]",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--pulse)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--paper)]",
    "disabled:pointer-events-none disabled:opacity-50",
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
    "[@media(any-pointer:coarse)]:min-h-[44px] [@media(any-pointer:coarse)]:min-w-[44px]",
    "[@media(max-width:639px)]:min-h-[44px] [@media(max-width:639px)]:min-w-[44px]",
  ].join(" "),
  {
    variants: {
      variant: {
        default:
          "bg-[var(--pulse)] text-[var(--on-accent)] hover:bg-[var(--pulse-strong)]",
        destructive:
          "bg-[var(--bad)] text-[var(--on-accent)] hover:bg-[var(--bad)]/90",
        outline:
          "border border-[var(--border-control)] bg-[var(--paper-2)] text-[var(--ink)] hover:bg-[var(--paper-3)]",
        secondary:
          "bg-[var(--paper-3)] text-[var(--ink)] hover:bg-[var(--paper-3)]/80",
        ghost:
          "text-[var(--ink)] hover:bg-[var(--paper-3)]",
        link:
          "text-[var(--pulse)] underline-offset-4 hover:underline",
        success:
          "bg-[var(--ok)] text-[var(--on-accent)] hover:bg-[var(--ok)]/90",
      },
      size: {
        default: "h-[var(--control-size)] px-[var(--control-padding-x)] py-[var(--control-padding-y)]",
        sm: "h-[var(--control-size)] px-[var(--control-padding-x-sm)] py-[var(--control-padding-y)] text-sm",
        lg: "h-[var(--control-size-lg)] px-[var(--control-padding-x-lg)]",
        icon: "h-[var(--control-size)] w-[var(--control-size)]",
        "icon-sm": "h-[var(--control-size)] w-[var(--control-size)]",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Render as child element (Radix Slot — useful for router Link wrappers). */
  asChild?: boolean;
  /** Show a spinner and disable the button while loading. */
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, loading = false, children, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        data-cds-button
        {...props}
        disabled={loading || props.disabled}
        aria-busy={loading}
      >
        {loading ? (
          <>
            <span
              className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent"
              aria-hidden="true"
            />
            {children}
          </>
        ) : (
          children
        )}
      </Comp>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
